// eTAMS — Travel advances (PDF page 4).
// Rebuilt layout; keeps every existing feature: drafts (edit / submit),
// image attachments, Submit ECRF against a released advance, Report / PDF,
// search + date filter + pagination, revised-amount visibility.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { notify, confirm } from '../../utils/notify';
import { expenseAPI, etamsAPI, uploadAttachment, openExpenseAttachment } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useListControls } from '../../components/ListControls';
import { advanceActorName, withActor, advanceOpenClosePill } from '../../utils/expenseStatus';
import { generateAdvanceReportPDF } from '../../utils/advanceReportPDF';
import {
  PageShell, PageHead, Stat, Card, Badge, Note, Field, Input, Select, Textarea, Modal, Kebab, Empty, Loading, Pager, Spin,
  VFlow, FileDrop, FileChip, money, fmtDate, fmtRange, fmtDateTime, isoDate, useQueryParam,
} from '../../components/ui';
import { advanceStatus, releaseStatus, attachmentsOf, MODES_OF_TRAVEL } from '../../utils/etams';

const MANUAL = '__manual__';
const emptyForm = () => ({
  journey: '', purpose: '', location: '', mode: 'Rail · AC3', startDate: '', endDate: '', estAmount: '',
  advAmount: '', preferredMode: 'Bank transfer', dateRequired: '', purposeAdv: '',
});
const emptyEcrfRow = () => ({ date: '', mode: 'Bus', from: '', to: '', transportAmt: '', details: '', lodgingAmt: '', lodgingDetails: '', miscAmt: '', miscDetails: '' });
const transportExpenseType = (mode) => {
  const m = (mode || '').toLowerCase();
  if (m.includes('air') || m.includes('flight')) return 'Air Ticket';
  if (m.includes('train') || m.includes('rail')) return 'Rail Fare';
  if (m.includes('bus')) return 'Bus Fare';
  if (m.includes('cab') || m.includes('taxi') || m.includes('auto')) return 'Taxi / Auto / Cab';
  return 'Local Conveyance';
};

function TravelAdvanceRequest() {
  const { user } = useAuth();
  const journeyParam = useQueryParam('journey');
  const [requests, setRequests] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  const [attachFiles, setAttachFiles] = useState([]);
  const [saving, setSaving] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [view, setView] = useState(null);
  const [submittingDraftId, setSubmittingDraftId] = useState(null);
  const [reportingId, setReportingId] = useState(null);
  const formRef = useRef();

  // ECRF
  const [ecrfFor, setEcrfFor] = useState(null);
  const [ecrfRows, setEcrfRows] = useState([emptyEcrfRow()]);
  const [ecrfRemarks, setEcrfRemarks] = useState('');
  const [ecrfFiles, setEcrfFiles] = useState([]);
  const [ecrfSaving, setEcrfSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      expenseAPI.listAdvances({ limit: 200 }),
      etamsAPI.listTravel({ scope: 'mine', status: 'APPROVED' }).catch(() => ({ requests: [] })),
    ]).then(([a, t]) => {
      setRequests(a.requests || []);
      setJourneys(t.requests || []);
    }).catch((e) => notify.error(e.message || 'Failed to load')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (journeyParam && journeys.length) selectJourney(journeyParam); }, [journeyParam, journeys.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Only the current user's own advances count on this page for employees;
  // approvers also see their team's advances (existing list behaviour).
  const mine = requests.filter((r) => String(r.requestedBy?._id || r.requestedBy) === String(user?.id));
  const filtered = filter === 'ALL' ? requests : filter === 'PENDING' ? requests.filter((r) => r.status.startsWith('PENDING')) : requests.filter((r) => r.status === filter);
  const lc = useListControls(filtered, {
    searchFields: [(r) => r.requestId, (r) => r.employeeName || r.requestedBy?.userName, 'purposeOfTravel', 'travelLocation', 'travelRequestNo'],
    dateField: 'createdAt', pageSize: 8,
  });

  const sum = (arr, f) => arr.reduce((s, r) => s + (Number(f(r)) || 0), 0);
  const nonDraft = mine.filter((r) => !['DRAFT', 'REJECTED'].includes(r.status));
  const stats = {
    requested: sum(nonDraft, (r) => r.advanceAmountRequested),
    pending: sum(mine.filter((r) => r.status.startsWith('PENDING')), (r) => r.advanceAmountRequested),
    approved: sum(mine.filter((r) => r.status === 'APPROVED'), (r) => r.approvedAmount ?? r.currentAmount ?? r.advanceAmountRequested),
    released: sum(mine, (r) => r.advanceReleasedAmount),
  };

  // ── Form ───────────────────────────────────────────────────────────────────
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const journey = journeys.find((j) => j._id === form.journey);
  const estimate = journey ? journey.estimatedTotal : Number(form.estAmount) || 0;
  const ceiling = journey ? journey.advanceCeiling || Math.floor(estimate / 3) : Math.floor(estimate / 3);

  function selectJourney(idv) {
    const j = journeys.find((x) => x._id === idv);
    setForm((p) => ({
      ...p, journey: idv,
      ...(j ? {
        purpose: j.purpose, location: j.destination, mode: j.modeOfTravel || p.mode, startDate: isoDate(j.departureDate), endDate: isoDate(j.returnDate),
        estAmount: j.estimatedTotal, advAmount: p.advAmount || j.advanceCeiling || '',
        dateRequired: p.dateRequired || isoDate(new Date(new Date(j.departureDate).getTime() - 86400000)),
      } : {}),
    }));
  }

  const resetForm = () => { setForm(emptyForm()); setEditId(null); setAttachFiles([]); };

  const handleSubmit = async (asDraft) => {
    const isManual = form.journey === MANUAL || !form.journey;
    if (!asDraft) {
      if (!form.journey) { notify.error('Select the approved journey (or choose "Not linked to a travel request")'); return; }
      if (!form.advAmount || Number(form.advAmount) <= 0) { notify.error('Enter the amount requested'); return; }
      if (!form.dateRequired) { notify.error('Select when the advance is needed by'); return; }
      if (!form.purposeAdv.trim()) { notify.error('Enter the reason for the advance'); return; }
      if (isManual && (!form.purpose || !form.location || !form.startDate || !form.endDate)) { notify.error('Fill the trip details'); return; }
      if (ceiling && Number(form.advAmount) > ceiling) {
        const res = await confirm.action('Above advance ceiling', `Policy ceiling is one-third of the estimated tour cost (${money(ceiling)}). Submit anyway with justification in the reason?`, 'Submit anyway', '#d08a1c');
        if (!res.isConfirmed) return;
      }
    }
    setSaving(asDraft ? 'draft' : 'submit');
    try {
      let attachments = [];
      if (attachFiles.length) {
        try { attachments = await Promise.all(attachFiles.map((f) => uploadAttachment(f, 'expense'))); } catch { notify.warn('Could not upload one or more attachments, continuing without them.'); }
      }
      const payload = {
        purposeOfTravel: form.purpose, travelLocation: form.location, modeOfTravel: form.mode,
        travelStartDate: form.startDate, travelEndDate: form.endDate,
        advanceAmountRequested: Number(form.advAmount), estimatedTotalExpense: Number(estimate) || undefined,
        purposeOfAdvance: form.purposeAdv, dateRequired: form.dateRequired, preferredMode: form.preferredMode,
        travelRequestId: journey ? journey._id : '', travelRequestNo: journey ? journey.requestNo : '', projectCode: journey?.projectCode,
        ...(attachments.length ? { attachments } : {}),
      };
      if (editId) {
        await expenseAPI.updateAdvance(editId, payload);
        if (!asDraft) { await expenseAPI.submitAdvance(editId); notify.success('Advance request submitted for approval!'); } else notify.success('Draft updated');
      } else {
        await expenseAPI.createAdvance({ ...payload, saveAsDraft: asDraft });
        notify.success(asDraft ? 'Saved as draft' : 'Advance request submitted — Reporting Manager → Accounts');
      }
      resetForm();
      load();
    } catch (e) {
      notify.error(e.message || 'Failed');
    } finally {
      setSaving(null);
    }
  };

  const handleEditDraft = (r) => {
    setEditId(r._id);
    setAttachFiles([]);
    setForm({
      journey: r.travelRequestId ? String(r.travelRequestId?._id || r.travelRequestId) : MANUAL,
      purpose: r.purposeOfTravel || '', location: r.travelLocation || '', mode: r.modeOfTravel || 'Rail · AC3',
      startDate: isoDate(r.travelStartDate), endDate: isoDate(r.travelEndDate), estAmount: r.estimatedTotalExpense || '',
      advAmount: r.advanceAmountRequested || '', preferredMode: r.preferredMode || 'Bank transfer', dateRequired: isoDate(r.dateRequired), purposeAdv: r.purposeOfAdvance || '',
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmitForApproval = async (r) => {
    const res = await confirm.action('Submit for approval?', 'This draft will be sent to your Reporting Manager for approval and can no longer be edited freely.', 'Yes, Submit', '#1479bd');
    if (!res.isConfirmed) return;
    setSubmittingDraftId(r._id);
    try { await expenseAPI.submitAdvance(r._id); notify.success('Advance request submitted for approval!'); load(); } catch (e) { notify.error(e.message || 'Could not submit — please edit the draft to fill in missing fields'); } finally { setSubmittingDraftId(null); }
  };

  const handleDownloadReport = async (r) => {
    setReportingId(r._id);
    try {
      await generateAdvanceReportPDF(r, async (s3Key) => { const { url } = await expenseAPI.getAttachmentUrl(s3Key); return url; });
    } catch (e) { notify.error(e.message || 'Could not generate the report'); } finally { setReportingId(null); }
  };

  // ── ECRF ───────────────────────────────────────────────────────────────────
  const openECRF = (r) => { setEcrfFor(r); setEcrfRows([emptyEcrfRow()]); setEcrfRemarks(''); setEcrfFiles([]); };
  const setEcrfRow = (i, k, v) => setEcrfRows((rows) => rows.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const ecrfRowTotal = (row) => (Number(row.transportAmt) || 0) + (Number(row.lodgingAmt) || 0) + (Number(row.miscAmt) || 0);
  const ecrfGrandTotal = ecrfRows.reduce((s, row) => s + ecrfRowTotal(row), 0);
  const ecrfReleased = Number(ecrfFor?.advanceReleasedAmount) || 0;
  const ecrfBalance = ecrfGrandTotal - ecrfReleased;
  const handleSubmitECRF = async () => {
    if (!ecrfRows.some((r) => ecrfRowTotal(r) > 0)) { notify.error('Add at least one Transport, Lodging/Fooding or Miscellaneous amount'); return; }
    setEcrfSaving(true);
    try {
      let attachments = [];
      if (ecrfFiles.length) {
        try { attachments = await Promise.all(ecrfFiles.map((f) => uploadAttachment(f, 'expense'))); } catch { notify.warn('Could not upload one or more attachments, submitting ECRF without them.'); }
      }
      const expenses = [];
      ecrfRows.forEach((row) => {
        const d = row.date || new Date().toISOString();
        if (Number(row.transportAmt) > 0) expenses.push({ expenseType: transportExpenseType(row.mode), date: d, description: row.details || [row.mode, row.from && row.to ? `${row.from} → ${row.to}` : row.from || row.to].filter(Boolean).join(' — '), amount: Number(row.transportAmt), gst: 0, total: Number(row.transportAmt) });
        if (Number(row.lodgingAmt) > 0) expenses.push({ expenseType: 'Hotel', date: d, description: row.lodgingDetails || 'Lodging / Fooding', amount: Number(row.lodgingAmt), gst: 0, total: Number(row.lodgingAmt) });
        if (Number(row.miscAmt) > 0) expenses.push({ expenseType: 'Miscellaneous', date: d, description: row.miscDetails || 'Miscellaneous', amount: Number(row.miscAmt), gst: 0, total: Number(row.miscAmt) });
      });
      await expenseAPI.createClaim({
        purposeOfTravel: ecrfFor.purposeOfTravel, reportTitle: ecrfFor.purposeOfTravel, travelLocation: ecrfFor.travelLocation, modeOfTravel: ecrfFor.modeOfTravel,
        travelStartDate: ecrfFor.travelStartDate, travelEndDate: ecrfFor.travelEndDate, expenses, linkedAdvanceId: ecrfFor._id,
        travelRequestId: ecrfFor.travelRequestId, travelRequestNo: ecrfFor.travelRequestNo, projectCode: ecrfFor.projectCode,
        saveAsDraft: false, declarationAccepted: true,
        ...(ecrfRemarks ? { remarks: ecrfRemarks } : {}), ...(attachments.length ? { attachments } : {}),
      });
      notify.success('ECRF submitted — the released advance has been adjusted against your expenses.');
      setEcrfFor(null);
      load();
    } catch (e) { notify.error(e.message || 'Could not submit ECRF'); } finally { setEcrfSaving(false); }
  };

  const isOwner = (r) => user && (String(r.requestedBy?._id || r.requestedBy) === String(user.id) || user.role === 'admin');
  const approvedJourneys = useMemo(() => journeys.filter((j) => j.status === 'APPROVED'), [journeys]);

  return (
    <PageShell section="Travel & Expenses">
      <PageHead title="Travel advances"
        actions={<button type="button" className="tse-btn lg" onClick={() => { resetForm(); formRef.current?.scrollIntoView({ behavior: 'smooth' }); }}>Request an advance</button>} />

      <div className="tse-grid g-4 mb-18">
        <Stat icon="bi-file-earmark-text" tone="blue" label="Requested" value={money(stats.requested)} loading={loading} onClick={() => setFilter('ALL')} />
        <Stat icon="bi-hourglass-split" tone="amber" label="Pending" value={money(stats.pending)} loading={loading} onClick={() => setFilter('PENDING')} />
        <Stat icon="bi-check-circle" tone="green" label="Approved" value={money(stats.approved)} loading={loading} onClick={() => setFilter('APPROVED')} />
        <Stat icon="bi-database" tone="gray" label="Released" value={money(stats.released)} loading={loading} />
      </div>

      <Note tone="info" big className="mb-18"><strong>Normal advance ceiling: one-third of estimated tour cost</strong></Note>

      <div className="tse-split wide-side">
        <Card navy bodyClass="flush" title="Advance register" actions={<span className="meta" style={{ color: 'rgba(255,255,255,.85)', fontSize: '0.782rem' }}>Showing {lc.paginated.length} of {lc.totalCount} advances</span>} style={{ minHeight: 520 }}>
          <div className="tse-toolbar" style={{ paddingBottom: 4 }}>
            <div className="tse-search" style={{ maxWidth: 280 }}><i className="bi bi-search" /><input placeholder="Search advances" value={lc.search} onChange={(e) => lc.setSearch(e.target.value)} /></div>
            <div className="tse-filter-pills">
              {[['ALL', 'All'], ['PENDING', 'Pending'], ['APPROVED', 'Approved'], ['DRAFT', 'Drafts'], ['REJECTED', 'Rejected']].map(([k, l]) => (
                <button type="button" key={k} className={`tse-filter-pill ${filter === k ? 'active' : ''}`} style={{ padding: '4px 10px' }} onClick={() => setFilter(k)}>{l}</button>
              ))}
            </div>
          </div>
          {loading ? <Loading /> : lc.paginated.length === 0 ? <Empty icon="bi-wallet2" title="No advances found" sub="Request an advance against an approved journey." /> : (
            <div className="tse-table-wrap">
              <table className="tse-table">
                <thead><tr><th>Advance No.</th><th>Journey Ref.</th><th>Destination</th><th className="num">Amount (₹)</th><th>Status</th><th>Release status</th><th>Action</th></tr></thead>
                <tbody>
                  {lc.paginated.map((r) => {
                    const st = advanceStatus(r);
                    const rs = releaseStatus(r);
                    const own = isOwner(r);
                    return (
                      <tr key={r._id}>
                        <td><button type="button" className="tse-link" onClick={() => setView(r)}>{r.requestId}</button>
                          {String(r.requestedBy?._id || r.requestedBy) !== String(user?.id) && <div className="tse-small tse-muted">{r.employeeName}</div>}</td>
                        <td>{r.travelRequestNo || <span className="tse-muted">—</span>}</td>
                        <td>{r.travelLocation}</td>
                        <td className="num">{money(r.status === 'APPROVED' && r.approvedAmount != null ? r.approvedAmount : r.advanceAmountRequested)}</td>
                        <td><Badge tone={st.tone} title={withActor(st.label, advanceActorName(r))}>{st.label}</Badge></td>
                        <td><Badge tone={rs.tone}>{rs.label}</Badge></td>
                        <td>
                          <Kebab items={[
                            { label: 'View details', icon: 'bi-eye', onClick: () => setView(r) },
                            { label: 'Edit draft', icon: 'bi-pencil', onClick: () => handleEditDraft(r), hidden: !(r.status === 'DRAFT' && own) },
                            { label: submittingDraftId === r._id ? 'Submitting…' : 'Submit for approval', icon: 'bi-send', onClick: () => handleSubmitForApproval(r), hidden: !(r.status === 'DRAFT' && own), disabled: submittingDraftId === r._id },
                            { label: 'Submit ECRF', icon: 'bi-file-earmark-text', onClick: () => openECRF(r), hidden: !(own && r.advanceReleasedAmount > 0 && !r.linkedClaimId) },
                            { label: 'ECRF submitted', icon: 'bi-check2-all', onClick: () => {}, disabled: true, hidden: !(own && r.advanceReleasedAmount > 0 && r.linkedClaimId) },
                            { label: reportingId === r._id ? 'Preparing PDF…' : 'Report / PDF', icon: 'bi-file-earmark-pdf', onClick: () => handleDownloadReport(r), disabled: reportingId === r._id },
                          ]} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={lc.page} pages={lc.totalPages} total={lc.totalCount} pageSize={lc.pageSize} setPage={lc.setPage} label="advances" />
        </Card>

        <div ref={formRef}>
          <Card navy title={editId ? 'Edit draft advance' : 'Request an advance'}>
            <div className="tse-stack" style={{ gap: 8 }}>
              <Field label="Approved journey" required>
                <Select value={form.journey} onChange={(e) => (e.target.value === MANUAL ? set('journey', MANUAL) : selectJourney(e.target.value))}
                  placeholder="Select approved journey"
                  options={[...approvedJourneys.map((j) => ({ value: j._id, label: `${j.requestNo} · ${j.destination} · ${fmtRange(j.departureDate, j.returnDate)}` })), { value: MANUAL, label: 'Not linked to a travel request' }]} />
              </Field>
              {form.journey === MANUAL && (
                <div className="tse-stack" style={{ gap: 8, padding: 8, background: '#f6f9fc', borderRadius: 8 }}>
                  <Field label="Purpose of travel" required><Input value={form.purpose} onChange={(e) => set('purpose', e.target.value)} /></Field>
                  <div className="tse-form-grid c2">
                    <Field label="Destination" required><Input value={form.location} onChange={(e) => set('location', e.target.value)} /></Field>
                    <Field label="Mode"><Select value={form.mode} onChange={(e) => set('mode', e.target.value)} options={MODES_OF_TRAVEL} /></Field>
                    <Field label="From" required><Input type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} /></Field>
                    <Field label="To" required><Input type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} /></Field>
                  </div>
                </div>
              )}
              <div className="tse-form-grid c2">
                <Field label="Estimated tour cost (₹)">
                  {form.journey === MANUAL ? <Input type="number" value={form.estAmount} onChange={(e) => set('estAmount', e.target.value)} /> : <Input value={estimate ? money(estimate) : ''} readOnly />}
                </Field>
                <Field label="Maximum advance (₹)"><Input value={estimate ? money(ceiling) : ''} readOnly /></Field>
              </div>
              <Field label="Amount requested (₹)" required><Input type="number" min="0" value={form.advAmount} onChange={(e) => set('advAmount', e.target.value)} placeholder="₹0" /></Field>
              {ceiling > 0 && Number(form.advAmount) > ceiling && <Note tone="warn">Above the one-third ceiling ({money(ceiling)}). Add a justification.</Note>}
              <div className="tse-form-grid c2">
                <Field label="Preferred mode" required><Select value={form.preferredMode} onChange={(e) => set('preferredMode', e.target.value)} options={['Bank transfer', 'Cash', 'UPI']} /></Field>
                <Field label="Needed by" required><Input type="date" value={form.dateRequired} onChange={(e) => set('dateRequired', e.target.value)} /></Field>
              </div>
              <Field label="Reason for advance" required><Textarea rows={2} value={form.purposeAdv} onChange={(e) => set('purposeAdv', e.target.value)} placeholder="e.g. Site travel cash requirement" /></Field>
              <Field label="Supporting documents (images, optional)">
                <FileDrop inline accept="image/*" sub="JPG / PNG (Max 10 MB each)" onFiles={(fs) => setAttachFiles((p) => [...p, ...fs])} />
                {attachFiles.map((f, i) => <FileChip key={i} file={f} onRemove={() => setAttachFiles((p) => p.filter((_, j) => j !== i))} />)}
              </Field>
              <button type="button" className="tse-btn lg block" onClick={() => handleSubmit(false)} disabled={!!saving}>{saving === 'submit' ? <Spin /> : null}{editId ? 'Update & submit' : 'Submit advance request'}</button>
              <div className="tse-row" style={{ justifyContent: 'space-between' }}>
                <button type="button" className="tse-link" onClick={() => handleSubmit(true)} disabled={!!saving}>{saving === 'draft' ? 'Saving…' : 'Save as draft'}</button>
                {editId && <button type="button" className="tse-link" onClick={resetForm}>Cancel edit</button>}
              </div>
              <div className="tse-row tse-small tse-muted" style={{ gap: 8 }}><i className="bi bi-info-circle-fill" style={{ color: '#1479bd' }} />Reporting manager approval and Accounts release required.</div>
            </div>
          </Card>
        </div>
      </div>

      {/* Details */}
      <Modal open={!!view} onClose={() => setView(null)} size="lg" title={view ? `Advance ${view.requestId}` : ''} sub={view?.purposeOfTravel}>
        {view && (() => {
          const st = advanceStatus(view); const oc = advanceOpenClosePill(view);
          return (
            <div className="tse-stack" style={{ gap: 8 }}>
              <div className="tse-row"><Badge tone={st.tone} lg>{withActor(st.label, advanceActorName(view))}</Badge><Badge tone={releaseStatus(view).tone} lg>{releaseStatus(view).label}</Badge>{oc && <Badge tone={oc.cls === 'status-approved' ? 'approved' : 'gray'}>{oc.label}</Badge>}</div>
              <div className="tse-form-grid c3">
                {[
                  ['Employee', `${view.employeeName} · ${view.department || ''}`], ['Journey ref.', view.travelRequestNo || '—'], ['Destination', view.travelLocation],
                  ['Mode', view.modeOfTravel], ['Travel dates', fmtRange(view.travelStartDate, view.travelEndDate)], ['Days', view.totalDays],
                  ['Requested', money(view.advanceAmountRequested)], ['Estimated total', money(view.estimatedTotalExpense)], ['Needed by', fmtDate(view.dateRequired)],
                  ['Preferred mode', view.preferredMode || '—'], ['Released', view.advanceReleasedAmount ? `${money(view.advanceReleasedAmount)} · ${fmtDate(view.advanceReleasedAt)}` : '—'], ['Submitted', fmtDate(view.createdAt)],
                ].map(([k, v]) => <div key={k}><div className="tse-muted tse-small">{k}</div><div className="tse-strong">{v}</div></div>)}
              </div>
              {view.status === 'APPROVED' && view.amountModified && view.approvedAmount != null && view.approvedAmount !== view.advanceAmountRequested && (
                <Note tone="warn">Approved amount {money(view.approvedAmount)} (revised during approval{view.amountModifiedBy?.userName ? ` by ${view.amountModifiedBy.userName}` : ''}).</Note>
              )}
              {view.purposeOfAdvance && <div><div className="tse-muted tse-small">Reason</div>{view.purposeOfAdvance}</div>}
              {attachmentsOf(view).length > 0 && (
                <div className="tse-grid g-2">{attachmentsOf(view).map((a, i) => <FileChip key={a.s3Key || i} file={a} onOpen={() => openExpenseAttachment(a).catch(() => notify.error('Could not open attachment'))} />)}</div>
              )}
              {(view.approvalHistory || []).length > 0 && (
                <VFlow items={view.approvalHistory.map((h) => ({ state: h.action === 'APPROVED' ? 'done' : 'rejected', title: `${h.action === 'APPROVED' ? 'Approved' : 'Rejected'} · ${h.role}`, sub: h.remarks, meta: <>{h.approverId?.userName || ''}<br />{fmtDateTime(h.actionAt)}</> }))} />
              )}
            </div>
          );
        })()}
      </Modal>

      {/* ECRF */}
      <Modal open={!!ecrfFor} onClose={() => setEcrfFor(null)} size="xl" title="Submit ECRF — Expense Claim & Reconciliation Form" sub={ecrfFor ? `${ecrfFor.requestId} · ${ecrfFor.purposeOfTravel}` : ''}
        footer={<><button type="button" className="tse-btn gray" onClick={() => setEcrfFor(null)}>Cancel</button><button type="button" className="tse-btn" onClick={handleSubmitECRF} disabled={ecrfSaving}>{ecrfSaving ? <Spin /> : <i className="bi bi-send" />}Submit ECRF</button></>}>
        {ecrfFor && (
          <div className="tse-stack" style={{ gap: 8 }}>
            <div className="tse-form-grid c4" style={{ background: '#f5f8fb', padding: 10, borderRadius: 8 }}>
              {[['Request No', ecrfFor.requestId], ['Date of request', fmtDate(ecrfFor.createdAt)], ['Employee ID', ecrfFor.employeeCode || '—'], ['Department', ecrfFor.department || '—'],
                ['Subject / Purpose', ecrfFor.purposeOfTravel], ['Requisitioner', ecrfFor.employeeName || user?.userName], ['Period', fmtRange(ecrfFor.travelStartDate, ecrfFor.travelEndDate)], ['Advance released', money(ecrfReleased)]]
                .map(([k, v]) => <div key={k}><div className="tse-muted tse-small">{k}</div><div className="tse-strong">{v}</div></div>)}
            </div>
            <div className="tse-label">Expense details — day-wise</div>
            {ecrfRows.map((row, i) => (
              <div key={i} className="tse-card" style={{ boxShadow: 'none' }}>
                <div className="tse-card-body tight">
                  <div className="tse-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
                    <strong><i className="bi bi-calendar-day" style={{ color: '#1479bd' }} /> Day {i + 1}</strong>
                    <span className="tse-row"><strong style={{ color: '#173b5f' }}>{money(ecrfRowTotal(row))}</strong>{ecrfRows.length > 1 && <button type="button" className="tse-btn xs danger-outline" onClick={() => setEcrfRows((rows) => rows.filter((_, j) => j !== i))}><i className="bi bi-trash" /></button>}</span>
                  </div>
                  <div className="tse-form-grid c4">
                    <Field label="Date"><Input type="date" value={row.date} onChange={(e) => setEcrfRow(i, 'date', e.target.value)} /></Field>
                    <Field label="Transport mode"><Select value={row.mode} onChange={(e) => setEcrfRow(i, 'mode', e.target.value)} options={['Bus', 'Pvt Cab', 'Taxi', 'Auto', 'Air', 'Train', 'Own Vehicle', 'Other']} /></Field>
                    <Field label="From"><Input value={row.from} onChange={(e) => setEcrfRow(i, 'from', e.target.value)} /></Field>
                    <Field label="To"><Input value={row.to} onChange={(e) => setEcrfRow(i, 'to', e.target.value)} /></Field>
                    <Field label="Transport amt (₹)"><Input type="number" value={row.transportAmt} onChange={(e) => setEcrfRow(i, 'transportAmt', e.target.value)} /></Field>
                    <Field label="Transport details"><Input value={row.details} onChange={(e) => setEcrfRow(i, 'details', e.target.value)} /></Field>
                    <Field label="Lodging/Fooding amt (₹)"><Input type="number" value={row.lodgingAmt} onChange={(e) => setEcrfRow(i, 'lodgingAmt', e.target.value)} /></Field>
                    <Field label="Lodging/Fooding details"><Input value={row.lodgingDetails} onChange={(e) => setEcrfRow(i, 'lodgingDetails', e.target.value)} /></Field>
                    <Field label="Misc. amt (₹)"><Input type="number" value={row.miscAmt} onChange={(e) => setEcrfRow(i, 'miscAmt', e.target.value)} /></Field>
                    <Field label="Misc. details" className="span-3"><Input value={row.miscDetails} onChange={(e) => setEcrfRow(i, 'miscDetails', e.target.value)} placeholder="e.g. Per Diem 250x4" /></Field>
                  </div>
                </div>
              </div>
            ))}
            <div><button type="button" className="tse-btn outline sm" onClick={() => setEcrfRows((r) => [...r, emptyEcrfRow()])}><i className="bi bi-plus-lg" />Add day</button></div>
            <Field label="Bills / receipts (images, optional)">
              <FileDrop inline accept="image/*" sub="JPG / PNG" onFiles={(fs) => setEcrfFiles((p) => [...p, ...fs])} />
              {ecrfFiles.map((f, i) => <FileChip key={i} file={f} onRemove={() => setEcrfFiles((p) => p.filter((_, j) => j !== i))} />)}
            </Field>
            <Field label="Remarks (optional)"><Textarea rows={2} value={ecrfRemarks} onChange={(e) => setEcrfRemarks(e.target.value)} /></Field>
            <div className="tse-amounts">
              <div><div className="k">Grand total</div><div className="v">{money(ecrfGrandTotal)}</div></div>
              <div><div className="k">Advance released</div><div className="v">{money(ecrfReleased)}</div></div>
              <div className="hl"><div className="k">{ecrfBalance >= 0 ? 'Balance payable to employee' : 'Balance recoverable from employee'}</div><div className="v" style={{ color: ecrfBalance >= 0 ? '#1e8a5a' : '#c0392b' }}>{money(Math.abs(ecrfBalance))}</div></div>
            </div>
            <Note tone="info">Routed through Reporting Manager → Finance. The released advance is adjusted against actual expenses and only the balance is settled.</Note>
          </div>
        )}
      </Modal>
    </PageShell>
  );
}
export default TravelAdvanceRequest;
